import { useRef, useState } from "react";
import { ArrowDown, ArrowUp, ExternalLink, Trash2 } from "lucide-react";

export type GalleryImage = {
  id: string;
  /** URL assinada do R2 ou endereço externo. */
  url: string;
  /** Nome do arquivo ou o endereço; vira o texto alternativo e o nome dos botões. */
  label: string;
  /** Externa: o CSP da SPA não carrega imagem de outro domínio, então mostra o link. */
  external: boolean;
};

type Props = {
  items: GalleryImage[];
  canEdit: boolean;
  onUpload(file: File): void;
  onAddUrl(url: string): void;
  onDelete(id: string): void;
  /** Nova ordem completa dos ids. */
  onReorder(ids: string[]): void;
  busy?: boolean;
  accept?: string;
  hint?: string;
};

const move = (ids: string[], from: number, to: number) => {
  const out = [...ids];
  const [x] = out.splice(from, 1);
  out.splice(to, 0, x!);
  return out;
};

/** Aba Imagens (RF-CAD-02): upload, URL externa, ordenação por botões (navegável por teclado) e exclusão. */
export function ImageGallery({
  items,
  canEdit,
  onUpload,
  onAddUrl,
  onDelete,
  onReorder,
  busy,
  accept = "image/png,image/jpeg,image/webp",
  hint,
}: Props) {
  const [url, setUrl] = useState("");
  const [urlError, setUrlError] = useState(false);
  const file = useRef<HTMLInputElement>(null);
  const ids = items.map((i) => i.id);
  return (
    <div>
      {items.length === 0 ? (
        <p className="text-muted">Nenhuma imagem.</p>
      ) : (
        <ul className="list-group mb-3" aria-label="Imagens do produto">
          {items.map((img, i) => (
            <li key={img.id} className="list-group-item d-flex align-items-center gap-3">
              {img.external ? (
                <a
                  href={img.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="d-inline-flex align-items-center gap-1 flex-grow-1 text-truncate"
                >
                  <ExternalLink size={16} aria-hidden="true" />
                  <span className="text-truncate">{img.label}</span>
                </a>
              ) : (
                <>
                  <img
                    src={img.url}
                    alt={img.label}
                    width={64}
                    height={64}
                    className="rounded border"
                    style={{ objectFit: "cover" }}
                  />
                  <span className="flex-grow-1 text-truncate">{img.label}</span>
                </>
              )}
              {i === 0 && <span className="badge text-bg-primary">Principal</span>}
              {canEdit && (
                <div className="btn-group btn-group-sm">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    aria-label={`Mover ${img.label} para cima`}
                    disabled={busy || i === 0}
                    onClick={() => onReorder(move(ids, i, i - 1))}
                  >
                    <ArrowUp size={16} aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    aria-label={`Mover ${img.label} para baixo`}
                    disabled={busy || i === items.length - 1}
                    onClick={() => onReorder(move(ids, i, i + 1))}
                  >
                    <ArrowDown size={16} aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-danger"
                    aria-label={`Excluir imagem ${img.label}`}
                    disabled={busy}
                    onClick={() => onDelete(img.id)}
                  >
                    <Trash2 size={16} aria-hidden="true" />
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {canEdit && (
        <div className="row g-2">
          <div className="col-md-5">
            <input
              ref={file}
              type="file"
              accept={accept}
              className="form-control"
              aria-label="Enviar imagem"
              disabled={busy}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) onUpload(f);
                e.target.value = "";
              }}
            />
          </div>
          <div className="col-md-7">
            <div className="input-group">
              <input
                type="url"
                className={`form-control${urlError ? " is-invalid" : ""}`}
                aria-label="Endereço da imagem"
                aria-invalid={urlError ? "true" : undefined}
                placeholder="https://..."
                value={url}
                onChange={(e) => {
                  setUrl(e.target.value);
                  setUrlError(false);
                }}
              />
              <button
                type="button"
                className="btn btn-outline-primary"
                disabled={busy || !url.trim()}
                onClick={() => {
                  const u = url.trim();
                  if (!/^https?:\/\/[^\s/]+\.[^\s/]+/i.test(u)) return setUrlError(true);
                  onAddUrl(u);
                  setUrl("");
                }}
              >
                Adicionar URL
              </button>
            </div>
          </div>
          {hint && <div className="form-text col-12">{hint}</div>}
        </div>
      )}
    </div>
  );
}
